import React from 'react';

export type CorGauge = 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'dark' | 'neutro';

interface GaugeIndicadorProps {
  valor: number;
  maximo: number;
  cor: CorGauge;
  sufixo?: string;
  casasDecimais?: number;
}

// Mesma paleta de `maestro-contabil.scss` — duplicada aqui porque o atributo SVG `stroke`
// precisa de uma cor literal (não aceita as classes .mc-badge--*).
const CORES: Record<CorGauge, string> = {
  primary: '#005e96', success: '#1bc5bd', warning: '#ffa800',
  danger: '#f64e60', info: '#8950fc', dark: '#181c32', neutro: '#a1a5b7',
};

const CX = 60;
const CY = 60;
const R = 44;
const ANGULO_INICIO = -135;
const ANGULO_FIM = 135;
const QUANTIDADE_TICKS = 10; // 11 marcações (0 a 10), a cada 10% do arco

const paraCartesiano = (angleDeg: number, raio: number) => {
  const angleRad = ((angleDeg - 90) * Math.PI) / 180;
  return { x: CX + raio * Math.cos(angleRad), y: CY + raio * Math.sin(angleRad) };
};

const descreverArco = (startAngle: number, endAngle: number, raio: number): string => {
  const inicio = paraCartesiano(endAngle, raio);
  const fim = paraCartesiano(startAngle, raio);
  const largeArcFlag = endAngle - startAngle <= 180 ? '0' : '1';
  return `M ${inicio.x} ${inicio.y} A ${raio} ${raio} 0 ${largeArcFlag} 0 ${fim.x} ${fim.y}`;
};

const anguloDaFracao = (fracao: number): number => ANGULO_INICIO + (ANGULO_FIM - ANGULO_INICIO) * fracao;

/**
 * Velocímetro (gauge radial) dos indicadores executivos do Cockpit — arco de 270° com
 * marcações de escala e ponteiro, no estilo de um painel automotivo, sem depender de
 * biblioteca de gráficos.
 */
const GaugeIndicador: React.FC<GaugeIndicadorProps> = ({ valor, maximo, cor, sufixo = '', casasDecimais = 0 }) => {
  const fracao = maximo > 0 ? Math.max(0, Math.min(1, Math.abs(valor) / maximo)) : 0;
  const anguloValor = anguloDaFracao(fracao);
  const corHex = CORES[cor];
  const idGradiente = `mc-gauge-grad-${cor}`;

  const ponteiro = paraCartesiano(anguloValor, R - 12);

  return (
    <div className='mc-gauge'>
      <svg viewBox='0 0 120 104' className='mc-gauge__svg'>
        <defs>
          <linearGradient id={idGradiente} x1='0%' y1='0%' x2='100%' y2='0%'>
            <stop offset='0%' stopColor={corHex} stopOpacity={.55} />
            <stop offset='100%' stopColor={corHex} stopOpacity={1} />
          </linearGradient>
        </defs>

        <path d={descreverArco(ANGULO_INICIO, ANGULO_FIM, R)} className='mc-gauge__trilha' />

        {Array.from({ length: QUANTIDADE_TICKS + 1 }, (_, i) => {
          const anguloTick = anguloDaFracao(i / QUANTIDADE_TICKS);
          const principal = i % 5 === 0;
          const p1 = paraCartesiano(anguloTick, R + 6);
          const p2 = paraCartesiano(anguloTick, principal ? R + 12 : R + 10);
          return <line key={i} x1={p1.x} y1={p1.y} x2={p2.x} y2={p2.y} className={`mc-gauge__tick${principal ? ' mc-gauge__tick--principal' : ''}`} />;
        })}

        {fracao > 0 && <path d={descreverArco(ANGULO_INICIO, anguloValor, R)} stroke={`url(#${idGradiente})`} className='mc-gauge__valor' />}

        <line x1={CX} y1={CY} x2={ponteiro.x} y2={ponteiro.y} className='mc-gauge__ponteiro' stroke={corHex} />
        <circle cx={CX} cy={CY} r='5' className='mc-gauge__cubo' fill={corHex} />
      </svg>
      <div className='mc-gauge__texto'>
        <strong>{valor.toFixed(casasDecimais)}{sufixo}</strong>
        <small>de {maximo.toFixed(casasDecimais)}{sufixo}</small>
      </div>
    </div>
  );
};

export default GaugeIndicador;
